import { useMutation, useQuery } from '@tanstack/react-query';
import { deleteUser } from 'firebase/auth';
import { useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { AppLink, useGo } from './Nav';
import { Banner, Button, ErrorText, Input } from './ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { auth } from '../lib/firebase';
import { deleteAccount, fetchDeletionBlockers } from '../queries/account';

/**
 * The way out, as a card. Shown on the settings page, where people look
 * for it, and on the account page beside the name.
 *
 * What stands in the way is asked up front, so a person with a subscription
 * to cancel reads that before typing "delete", not after. The API row goes
 * first; then the sign-in itself, which Firebase only allows after a recent
 * sign-in, and if it refuses the person is signed out and told to sign in
 * once more and finish, since the next API call would recreate them.
 */
export function DeleteAccount() {
  const { signOut } = useAuth();
  const go = useGo();
  const [confirm, setConfirm] = useState('');
  const deletion = useQuery({ queryKey: ['me', 'deletion'], queryFn: fetchDeletionBlockers });
  const blockers = deletion.data?.blockers ?? [];

  const remove = useMutation({
    mutationFn: async () => {
      await deleteAccount();
      const current = auth.currentUser;
      if (current) {
        try {
          await deleteUser(current);
        } catch (cause) {
          await signOut();
          const code = (cause as { code?: string })?.code;
          throw new Error(
            code === 'auth/requires-recent-login'
              ? 'Your Slate data is deleted. Sign in once more and delete your account again to remove the sign-in itself.'
              : errorMessage(cause),
          );
        }
      }
    },
    onSuccess: () => {
      track('Account Deleted');
      go('/login', { replace: true });
    },
  });

  const canDelete = confirm.trim().toLowerCase() === 'delete' && blockers.length === 0;

  return (
    <section className="card p-4 sm:p-5">
      <h2 className="text-h4 text-chalk">Delete account</h2>
      <p className="mt-1 text-body text-chalk-dim">
        Removes your recordings, your share links, and any team that only has you in it, and signs
        out every Mac. A team subscription has to be cancelled first, and a team with other people
        in it needs another owner before you can go.
      </p>
      {blockers.length > 0 ? (
        <div className="mt-4">
          <Banner tone="caution" title="Before your account can be deleted">
            <ul className="mt-1 space-y-1">
              {blockers.map((blocker) => (
                <li key={`${blocker.kind}-${blocker.id}`}>
                  {blocker.kind === 'subscription' ? (
                    <>
                      Cancel the subscription for <strong className="text-chalk">{blocker.name}</strong>.{' '}
                      <AppLink to={`/org/${blocker.slug}/billing`} className="text-signal hover:underline">
                        Manage billing
                      </AppLink>
                    </>
                  ) : (
                    <>
                      Make someone else an owner of <strong className="text-chalk">{blocker.name}</strong>.{' '}
                      <AppLink to={`/org/${blocker.slug}/members`} className="text-signal hover:underline">
                        Members
                      </AppLink>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </Banner>
        </div>
      ) : null}
      <div className="mt-4 max-w-xs">
        <Input
          label='Type "delete" to confirm'
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="off"
        />
      </div>
      <Button
        variant="danger"
        className="mt-4"
        disabled={!canDelete}
        busy={remove.isPending}
        onClick={() => remove.mutate()}
      >
        Delete my account
      </Button>
      {remove.isError ? (
        <div className="mt-2">
          <ErrorText>
            {remove.error instanceof ApiError && remove.error.status === 409
              ? remove.error.message
              : errorMessage(remove.error)}
          </ErrorText>
        </div>
      ) : null}
    </section>
  );
}
